import { supabase } from './supabase';
import { getCurrentUser } from './auth';

/**
 * WHO ADDED THIS TAG, AND THEREFORE WHO MAY TAKE IT BACK OFF.
 *
 * `book_tags` records `source` — author / api / reader — and, until
 * migration 20260906030000, recorded nothing else about who wrote a row. So
 * "let a reader remove a tag they added" had no data to run on: the database
 * could say a reader added it, and could not say WHICH reader.
 *
 * That migration adds one nullable column, `book_tags.added_by`, and one RLS
 * policy that allows a delete when `added_by = auth.uid()`. This file is the
 * only place in the client that writes or acts on that column, so the rule
 * lives in one place rather than being restated at each of the three screens
 * where a reader can tag a book.
 *
 * REMOVAL IS PER ENTRY POINT, AND THE RULE IS ENFORCED AT THE WRITE.
 *
 * Not every reader tag is removable, and the four entry points differ:
 *
 *   profile blocks       REMOVABLE. "To Know Me" and "If You Like What I Like"
 *   (ProfileBlocks.jsx)  both write through insertReaderTropes below, and both
 *                        show the ✕ panel.
 *   5-star popup         NOT removable.
 *   community composer   NOT removable. It is a feed post, not a profile block.
 *   CSV import           NOT removable. Bulk, with no realistic per-tag undo.
 *
 * The enforcement is that the three non-removable paths NEVER CALL
 * readerTagCredit(), so their rows carry added_by NULL and no policy on earth
 * lets anyone delete them. It is not a hidden button.
 *
 * That distinction is the whole design, so it is worth being explicit about
 * why. If every path recorded an owner and the ✕ were merely hidden on some
 * screens, then the ONE panel that does show ✕ buttons would show them for
 * tags added through the popup and the feed too — because the panel lists
 * every tag on the book and offers a ✕ on any row this reader owns. The rule
 * would be broken by the very screen meant to honour it. Deciding at the write
 * needs no extra column, no per-row flag and no per-screen list: a row is
 * removable exactly when somebody chose to record who added it.
 *
 * TWO THINGS THAT ARE EASY TO GET WRONG:
 *
 * 1. NULL means "nobody recorded it", not "added by nobody". Every row on the
 *    platform today is NULL, because the column did not exist when they were
 *    written. Those tags are permanently un-removable by any reader, and that
 *    is the correct outcome — there is no way to work out who added them, and
 *    guessing would hand somebody the power to delete a tag they never added.
 *    NULL is now also how the three non-removable entry points above are
 *    enforced, so the same value carries two meanings: "written before we
 *    recorded owners" and "written somewhere that does not offer removal".
 *    Neither is removable, so nothing downstream has to tell them apart.
 *
 * 1b. ONE ROW HOLDS ONE OWNER, so "two readers added the same tag" is not
 *    something this column can express. book_tags has UNIQUE (book_id,
 *    trope_id) and every reader write upserts with ignoreDuplicates, so the
 *    SECOND reader's write is a silent no-op and they are never recorded.
 *    The practical consequence: when the recorded owner removes their tag, the
 *    row goes, including any unrecorded agreement. Splitting that apart needs
 *    a second table keyed on (book_tag_id, user_id), which is real machinery
 *    for a case nobody has reported. Written down here rather than built.
 *
 * 2. THE DELETE IS GATED IN THE DATABASE, NOT HERE. `canRemove()` decides what
 *    to draw; the RLS policy decides what happens. If they ever disagree, the
 *    policy wins and the row survives — which is why removeTagIAdded() checks
 *    how many rows actually went, rather than assuming its own filter held.
 *    A delete blocked by RLS is not an error: Postgres reports zero rows
 *    removed and says nothing, which is exactly how this whole feature was
 *    silently absent before the policy existed.
 */

/**
 * The `added_by` value for a row this reader is writing right now.
 *
 * Returns an object to spread into the insert rather than a bare id, so a
 * signed-out write omits the column entirely instead of writing an explicit
 * null — the two are the same in the table, but the caller reads better.
 */
export async function readerTagCredit() {
  const user = await getCurrentUser();
  return user?.id ? { added_by: user.id } : {};
}

/**
 * Can this viewer take this tag back off?
 *
 * @param {{ added_by?: string|null }} tagRow  a book_tags row
 * @param {string|null|undefined} viewerId     the signed-in user's id
 */
export function canRemove(tagRow, viewerId) {
  return Boolean(viewerId && tagRow?.added_by && tagRow.added_by === viewerId);
}

/**
 * Remove one tag this reader added.
 *
 * The `.eq('added_by', ...)` is deliberately redundant with the RLS policy.
 * Two independent statements of the same rule cost one line and mean a policy
 * that is later loosened by accident does not immediately become a way to
 * delete other people's tags from this screen.
 *
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export async function removeTagIAdded(tagRowId) {
  const user = await getCurrentUser();
  if (!user?.id) return { ok: false, reason: 'You need to be signed in to remove a tag.' };

  const { data, error } = await supabase
    .from('book_tags')
    .delete()
    .eq('id', tagRowId)
    .eq('added_by', user.id)
    .select('id');

  if (error) return { ok: false, reason: error.message };

  // Zero rows is the important case and it is NOT an error from Postgres.
  // It means the row was not this reader's to remove, or was already gone.
  // Reported rather than swallowed, because "nothing happened and the chip is
  // still there" is precisely the silent failure this codebase exists to keep
  // out — the reader would press it again, and again.
  if (!data || data.length === 0) {
    return { ok: false, reason: 'That tag was not removed, it is not recorded as one you added.' };
  }

  return { ok: true };
}
